import { oids } from "@/configs/Settings";
import { BundlePatientSummary } from "@/services/fhir";
import type { PatientSummaryMhdEntryFilter } from "@/services/fhir/patientsummary/BundlePatientSummary";
import { HTTPResponseStatus } from "@/utils/enum";
import { useFhirData } from "@/utils/useFhirData";

import { getDependentIdentifier } from "./getDependentIdentifier";

/**
 * Recorte do Patient Summary no MHD (`AuxiliarData.PatientSummary` + `entries`):
 * só dispara o GET de QuestionnaireResponse — alinha a `PatientSummary.tsx`
 * (questionários do portal, sem puxar Observation, DocumentReference, etc.).
 */
export const PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY: PatientSummaryMhdEntryFilter[] =
  ["QuestionnaireResponse"];

/**
 * Mesma URL de extensão usada em `PatientQuestionnaireTasksSection` e no módulo
 * questionnaire-builder para filtrar respostas “do portal” (uso clínico tipado).
 */
const QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL =
  "http://fluxmed.com.br/StructureDefinition/questionnaire-usage-type";

export type ConsentReceivedLite = {
  id?: string;
  purpose?: string;
};

export type DependentQuestionnaireSummaryRow = {
  /** Patient/... do dependente (sujeito clínico). */
  subjectPatientReference: string;
  /** Nome amigável quando existir (ex.: display do RelatedPerson). */
  subjectPatientDisplayName?: string;
  /** Bundle Patient Summary bruto (mesmo formato do titular) para diálogos que reutilizam `useFhirData.extractPatientSummaryResource`. */
  bundle: fhir5.Bundle;
  /** Entradas `Bundle.entry` cujo `resource` é QuestionnaireResponse com extension de usage-type. */
  filteredEntries: any[];
};

/**
 * Monta `Patient/{logicalId}` a partir do recurso Patient do dependente retornado
 * pelo fluxo de RelatedPerson (id lógico ou identifier string).
 */
export function dependentPatientReferenceFromRecord(
  dependent: any,
  oidCpfPrefix: string,
): string | undefined {
  const rawId = dependent?.id != null ? String(dependent.id).trim() : "";
  if (rawId) {
    return rawId.startsWith("Patient/") ? rawId : `Patient/${rawId}`;
  }
  const depIdent = getDependentIdentifier(dependent);
  if (!depIdent) return undefined;
  if (depIdent.includes("-")) return `Patient/${depIdent}`;
  return `Patient/${oidCpfPrefix}-${depIdent}`;
}

function filterQuestionnaireResponseEntriesWithUsageType(
  bundle: fhir5.Bundle | undefined,
): any[] {
  if (!bundle) return [];
  const questionnaireData = useFhirData.extractPatientSummaryResource(
    bundle,
    "QuestionnaireResponse",
  );
  const questionnaireResponseEntries = questionnaireData?.[0]?.entry ?? [];
  return (
    questionnaireResponseEntries.filter((bundleEntry: any) => {
      const extensions = bundleEntry?.resource?.extension;
      if (!Array.isArray(extensions)) return false;
      return extensions.some(
        (extension: any) =>
          extension?.url === QUESTIONNAIRE_USAGE_TYPE_EXTENSION_URL,
      );
    }) ?? []
  );
}

/**
 * Identificador enviado ao MHD/PatientSummary: alinhar a `VetAppointments` e ao
 * `Patient.id` do RelatedPerson — `getDependentIdentifier` usa split("-")[1] no
 * `identifier` string e quebra IDs compostos (comuns em pets).
 */
export function patientSummaryIdentifierFromRelatedDependent(
  dependente: any,
): string | undefined {
  const rawId = dependente?.id != null ? String(dependente.id).trim() : "";
  if (rawId) {
    const noPrefix = rawId.replace(/^Patient\//, "").trim();
    if (noPrefix) return noPrefix;
  }
  const fromFhirIdentifier = dependente?.identifier?.[0]?.value;
  if (
    typeof fromFhirIdentifier === "string" &&
    fromFhirIdentifier.trim() !== ""
  ) {
    return fromFhirIdentifier.trim();
  }
  if (
    typeof dependente?.identifier === "string" &&
    dependente.identifier.trim() !== ""
  ) {
    return dependente.identifier.trim();
  }
  return getDependentIdentifier(dependente);
}

/**
 * Para cada dependente com consentimento “received” (purpose), busca o Patient Summary
 * e devolve questionários respondidos filtrados — espelha a filosofia de
 * `fetchDependentAppointments` (sem consent → sem request).
 */
export async function fetchDependentPatientSummaryQuestionnaireEntries({
  dependents,
  receivedConsents,
}: {
  dependents: any[] | undefined;
  receivedConsents: ConsentReceivedLite[] | undefined;
}): Promise<DependentQuestionnaireSummaryRow[]> {
  const safeDependents = dependents ?? [];
  const safeConsents = receivedConsents ?? [];

  const rows = await Promise.all(
    safeDependents.map(async (dependente) => {
      const dependenteId = getDependentIdentifier(dependente);
      if (!dependenteId) return null;

      const purpose = safeConsents.find((c) => c?.id === dependenteId)?.purpose;
      if (!purpose) return null;

      const subjectPatientReference = dependentPatientReferenceFromRecord(
        dependente,
        oids.cpf,
      );
      if (!subjectPatientReference) return null;

      const bundleResponse = await BundlePatientSummary({
        identifier: dependenteId,
        purposeofuse: purpose,
        entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
      });

      if (
        typeof bundleResponse === "object" &&
        bundleResponse &&
        "status" in bundleResponse &&
        bundleResponse.status === HTTPResponseStatus.Forbidden
      ) {
        return null;
      }

      const bundle = bundleResponse as fhir5.Bundle;
      const filteredEntries =
        filterQuestionnaireResponseEntriesWithUsageType(bundle);
      const subjectPatientDisplayName = String(
        dependente?.name?.[0]?.text ?? "",
      ).trim();

      return {
        subjectPatientReference,
        subjectPatientDisplayName: subjectPatientDisplayName || undefined,
        bundle,
        filteredEntries,
      } satisfies DependentQuestionnaireSummaryRow;
    }),
  );

  return rows.filter(Boolean) as DependentQuestionnaireSummaryRow[];
}

/**
 * FluxVet (tutor): carrega respostas de questionário dos pets sem consentimento
 * “received” e sem `purpose` por dependente. `purposeofuse` em `BundlePatientSummary`
 * usa o mesmo valor técnico do titular humano (“Atendimento”) — apenas parâmetro de API.
 */
export async function fetchVerifiedPetQuestionnaireSummaryEntries(
  verifiedDependentPatients: any[] | undefined,
): Promise<DependentQuestionnaireSummaryRow[]> {
  const safeDependents = verifiedDependentPatients ?? [];

  const rows = await Promise.all(
    safeDependents.map(async (dependente) => {
      const dependenteId = patientSummaryIdentifierFromRelatedDependent(dependente);
      if (!dependenteId) return null;

      const subjectPatientReference = dependentPatientReferenceFromRecord(
        dependente,
        oids.cpf,
      );
      if (!subjectPatientReference) return null;

      const bundleResponse = await BundlePatientSummary({
        identifier: dependenteId,
        purposeofuse: "Atendimento",
        entries: PATIENT_SUMMARY_ENTRIES_QUESTIONNAIRE_RESPONSE_ONLY,
      });

      if (
        typeof bundleResponse === "object" &&
        bundleResponse &&
        "status" in bundleResponse &&
        bundleResponse.status === HTTPResponseStatus.Forbidden
      ) {
        return null;
      }

      const bundle = bundleResponse as fhir5.Bundle;
      const filteredEntries =
        filterQuestionnaireResponseEntriesWithUsageType(bundle);
      const subjectPatientDisplayName = String(
        dependente?.name?.[0]?.text ?? "",
      ).trim();

      return {
        subjectPatientReference,
        subjectPatientDisplayName: subjectPatientDisplayName || undefined,
        bundle,
        filteredEntries,
      } satisfies DependentQuestionnaireSummaryRow;
    }),
  );

  return rows.filter(Boolean) as DependentQuestionnaireSummaryRow[];
}
